import { useCallback, useState } from 'react';
export var EMOTES_PER_ROW = 4;
var ROWS_PER_PAGE_KEY_EVENT = 3;
var GridNavigationHotkeyEvent;
(function (GridNavigationHotkeyEvent) {
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["RightNeighbourCell"] = 0] = "RightNeighbourCell";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["LeftNeighbourCell"] = 1] = "LeftNeighbourCell";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["TopNeighbourCell"] = 2] = "TopNeighbourCell";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["BottomNeighbourCell"] = 3] = "BottomNeighbourCell";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["FirstCellInRow"] = 4] = "FirstCellInRow";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["FirstCellInTable"] = 5] = "FirstCellInTable";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["LastCellInRow"] = 6] = "LastCellInRow";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["LastCellInTable"] = 7] = "LastCellInTable";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["PageDown"] = 8] = "PageDown";
    GridNavigationHotkeyEvent[GridNavigationHotkeyEvent["PageUp"] = 9] = "PageUp";
})(GridNavigationHotkeyEvent || (GridNavigationHotkeyEvent = {}));
function keyboardEventToTableNavigationEvent(event) {
    switch (event.key) {
        case 'ArrowRight':
            return GridNavigationHotkeyEvent.RightNeighbourCell;
        case 'ArrowLeft':
            return GridNavigationHotkeyEvent.LeftNeighbourCell;
        case 'ArrowDown':
            return GridNavigationHotkeyEvent.BottomNeighbourCell;
        case 'ArrowUp':
            return GridNavigationHotkeyEvent.TopNeighbourCell;
        case 'Home':
            if (event.getModifierState('Control')) {
                return GridNavigationHotkeyEvent.FirstCellInTable;
            }
            return GridNavigationHotkeyEvent.FirstCellInRow;
        case 'End':
            if (event.getModifierState('Control')) {
                return GridNavigationHotkeyEvent.LastCellInTable;
            }
            return GridNavigationHotkeyEvent.LastCellInRow;
        case 'PageUp':
            return GridNavigationHotkeyEvent.PageUp;
        case 'PageDown':
            return GridNavigationHotkeyEvent.PageDown;
        default:
            break;
    }
}
/**
 * Provides an implementation of keyboard support for a grid. We assume that the data is structured as a one-dimensional array of length `itemCount`.
 *
 * TODO: If we want to use this hook for grids besides the emote selector in the future, we should take in the number of items per row.
 */
export function useGridNavigationHotkeys(itemCount) {
    var _a = useState(null), focusedIndex = _a[0], setFocusedIndex = _a[1];
    // Defaulting to zero since we want the roving tab index derived from this
    // to start at the first element in the array.
    var _b = useState(0), lastFocusedIndex = _b[0], setLastFocusedIndex = _b[1];
    var onKeyDown = useCallback(function (e) {
        if (focusedIndex !== null) {
            var navigationEvent = keyboardEventToTableNavigationEvent(e);
            var columnIndex = focusedIndex % EMOTES_PER_ROW;
            var targetIndex = focusedIndex;
            switch (navigationEvent) {
                case GridNavigationHotkeyEvent.RightNeighbourCell:
                    e.preventDefault();
                    e.stopPropagation();
                    if (focusedIndex < itemCount &&
                        columnIndex !== EMOTES_PER_ROW - 1) {
                        targetIndex = focusedIndex + 1;
                    }
                    break;
                case GridNavigationHotkeyEvent.LeftNeighbourCell:
                    e.preventDefault();
                    e.stopPropagation();
                    if (focusedIndex > 0 && columnIndex !== 0) {
                        targetIndex = focusedIndex - 1;
                    }
                    break;
                case GridNavigationHotkeyEvent.TopNeighbourCell:
                    e.preventDefault();
                    e.stopPropagation();
                    if (focusedIndex - EMOTES_PER_ROW >= 0) {
                        targetIndex = focusedIndex - EMOTES_PER_ROW;
                    }
                    break;
                case GridNavigationHotkeyEvent.BottomNeighbourCell:
                    e.preventDefault();
                    e.stopPropagation();
                    if (focusedIndex + EMOTES_PER_ROW < itemCount) {
                        targetIndex = focusedIndex + EMOTES_PER_ROW;
                    }
                    break;
                case GridNavigationHotkeyEvent.PageUp:
                    e.preventDefault();
                    var pageUpIndex = focusedIndex - EMOTES_PER_ROW * ROWS_PER_PAGE_KEY_EVENT;
                    targetIndex = pageUpIndex >= 0 ? pageUpIndex : 0;
                    break;
                case GridNavigationHotkeyEvent.PageDown:
                    e.preventDefault();
                    var pageDownIndex = focusedIndex + EMOTES_PER_ROW * ROWS_PER_PAGE_KEY_EVENT;
                    targetIndex =
                        pageDownIndex < itemCount ? pageDownIndex : itemCount - 1;
                    break;
                case GridNavigationHotkeyEvent.FirstCellInRow:
                    e.preventDefault();
                    targetIndex = focusedIndex - columnIndex;
                    break;
                case GridNavigationHotkeyEvent.FirstCellInTable:
                    e.preventDefault();
                    targetIndex = 0;
                    break;
                case GridNavigationHotkeyEvent.LastCellInRow:
                    e.preventDefault();
                    var lastCellIndex = focusedIndex + (EMOTES_PER_ROW - 1 - columnIndex);
                    targetIndex =
                        lastCellIndex < itemCount ? lastCellIndex : itemCount - 1;
                    break;
                case GridNavigationHotkeyEvent.LastCellInTable:
                    e.preventDefault();
                    targetIndex = itemCount - 1;
                    break;
                default:
                    break;
            }
            if (targetIndex !== focusedIndex) {
                setFocusedIndex(targetIndex);
            }
        }
    }, [focusedIndex, itemCount]);
    var clearFocusedIndex = useCallback(function () {
        if (focusedIndex !== null) {
            setLastFocusedIndex(focusedIndex);
        }
        setFocusedIndex(null);
    }, [focusedIndex]);
    return {
        /** The current focused index  */
        focusedIndex: focusedIndex,
        /**
         * The last index that was focused.
         *
         * This is useful for implementing a roving tab-index. e.g.
         * ```
         * {items.map((item, index) =>
         *    <Component
         *      {...props}
         *      tabIndex={focusedIndex === null && lastFocusedIndex === i ? 0 : -1}
         *    />
         * )}
         * ```
         */
        lastFocusedIndex: lastFocusedIndex,
        /** Add this to your grid items to handle keyboard events. */
        onKeyDown: onKeyDown,
        /**
         * Sets the focus index.
         *
         * This is useful for implementing a roving tab-index. e.g.
         * ```
         * {items.map((item, index) =>
         *    <Component
         *      {...props}
         *      onFocus={() =>
         *        focusedIndex === null &&
         *        lastFocusedIndex === index &&
         *        setFocusedIndex(index)
         *      }
         *    />
         * )}
         * ```
         *
         */
        setFocusedIndex: setFocusedIndex,
        /**
         * Clears the focus index and updates what the last focused index was.
         *
         * You should add this to your currently focused grid item. e.g.
         * ```
         * {items.map((item, index) =>
         *    <Component
         *      {...props}
         *      onBlur={() => focusedIndex === i && clearFocusedIndex()}
         *    />
         * )}
         * ```
         */
        clearFocusedIndex: clearFocusedIndex,
    };
}
//# sourceMappingURL=hotkeys.js.map